import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { SiteStatusDto } from '@mafia/core';
import { api } from '@/lib/api';
import { setSiteClosed, useSite } from '@/store/site';
import { useAuth } from '@/store/auth';
import { connectSocket, disconnectSocket } from '@/lib/socket';
import { Layout } from '@/components/Layout';
import { GuestOnly, RequireAdmin, RequireAuth } from '@/components/guards';
import { Toaster } from '@/components/ui';
import { AuthShell, LoginPage, RegisterPage, ForgotPage, ResetPage } from '@/pages/auth/AuthPages';
import { RoomsPage } from '@/pages/Rooms';
import { RoomPage } from '@/pages/Room';
import { ProfilePage, MyProfileRedirect } from '@/pages/Profile';
import { GameResultPage } from '@/pages/GameResult';
import { RatingPage } from '@/pages/Rating';
import { AdminLayout } from '@/pages/admin/AdminLayout';
import { AdminDashboard } from '@/pages/admin/Dashboard';
import { AdminUsers } from '@/pages/admin/Users';
import { AdminUserDetail } from '@/pages/admin/UserDetail';
import { AdminGames } from '@/pages/admin/Games';
import { AdminGameLog } from '@/pages/admin/GameLog';
import { SiteClosedPage } from '@/pages/SiteClosed';
import { ConsentPage, PrivacyPage } from '@/pages/Legal';

export function App() {
  const { status, init, user } = useAuth();
  const closed = useSite((s) => s.closed);
  const location = useLocation();

  useEffect(() => {
    void init();
  }, [init]);

  // Гостю сразу показываем заглушку, если сайт закрыт. Вошедшим это скажет сервер (ответ SITE_CLOSED).
  useEffect(() => {
    if (status !== 'guest') return;
    api<SiteStatusDto>('/api/site/status')
      .then(({ open }) => setSiteClosed(!open))
      .catch(() => {});
  }, [status]);

  useEffect(() => {
    if (status === 'authed') connectSocket();
    if (status === 'guest') disconnectSocket();
  }, [status]);

  // Пока на сайте нет администратора, игрокам — заглушка; страница входа остаётся для админов.
  // Восстановление пароля доступно всегда: иначе админ, забывший пароль, не откроет сайт.
  const alwaysOpen = ['/login', '/forgot-password', '/reset-password', '/privacy', '/consent'].includes(location.pathname);
  if (closed && user?.role !== 'ADMIN' && !alwaysOpen) {
    return (
      <>
        <Toaster />
        <SiteClosedPage />
      </>
    );
  }

  return (
    <>
      <Toaster />
      <Routes>
        <Route element={<GuestOnly />}>
          <Route element={<AuthShell />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPage />} />
          </Route>
        </Route>
        <Route element={<AuthShell />}>
          <Route path="/reset-password" element={<ResetPage />} />
        </Route>
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/consent" element={<ConsentPage />} />

        <Route element={<RequireAuth />}>
          <Route element={<Layout />}>
            <Route path="/" element={<RoomsPage />} />
            <Route path="/room/:id" element={<RoomPage />} />
            <Route path="/profile" element={<MyProfileRedirect />} />
            <Route path="/profile/:id" element={<ProfilePage />} />
            <Route path="/games/:id" element={<GameResultPage />} />
            <Route path="/rating" element={<RatingPage />} />
            <Route element={<RequireAdmin />}>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="users/:id" element={<AdminUserDetail />} />
                <Route path="games" element={<AdminGames />} />
                <Route path="games/:id" element={<AdminGameLog />} />
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
